interface ApiEndpoints {
  AUTH: {
    HEALTH: string;
    LOGIN: string;
    REGISTER: string;
    REFRESH_TOKEN: string;
    FORGOT_PASSWORD: string;
    RESET_PASSWORD: string;
    GOOGLE_AUTH: string;
    LOGOUT: string;
    ME: string;
  };
  USER: {
    PROFILE: string;
    UPDATE_PROFILE: string;
  };
  BUSINESS: {
    ADD: string;
    UPDATE: (id: string | number) => string;
    GET_ALL: string;
    GET_BY_ID: (id: string | number) => string;
    ADD_SELF: string;
    ADD_CLIENT: string;
    GET_CLIENT_DETAILS: string;
    GET_SELF_DETAILS: string;
  };
  BANK: {
    ADD: string;
    UPDATE: (id: string | number) => string;
    GET_ALL: string;
    GET_BY_ID: (id: string | number) => string;
    DELETE: (id: string | number) => string;
  };
  INVOICE: {
    CREATE: string;
    GET_ALL: string;
    GET_BY_ID: (id: string | number) => string;
    SEARCH: string;
    DRAFT: string;
    IN_PROGRESS: string;
    UPLOAD: string;
    OCR_STATUS: string;
    JOURNEY: (num: string | number) => string;
    JOURNEY_COMPLETE: (num: string | number) => string;
  };
  SUBSCRIPTION: {
    CONFIGURATIONS: string;
    ALL_CONFIGURATIONS: string;
    MY: string;
    MY_ACTIVE: string;
    UPGRADE: string;
  };
  SUPPORT: {
    INFO: string;
  };
  ADMIN: {
    USERS: string;
    USER_DETAIL: (userId: string | number) => string;
    USER_INVOICES: (userId: string | number) => string;
    USER_BUSINESSES: (userId: string | number) => string;
    USER_CLIENTS: (userId: string | number) => string;
    USER_OVERVIEW: (userId: string | number) => string;
    USER_INACTIVATE: (userId: string | number) => string;
    USER_DELETE: (userId: string | number) => string;
    USER_REVOKE_DELETE: (userId: string | number) => string;
    USER_PERMANENT_DELETE: (userId: string | number) => string;
    SUPPORT_INFO: string;
  };
}

interface ApiConfig {
  BASE_URL: string | undefined;
  ENDPOINTS: ApiEndpoints;
  HEADERS: {
    CONTENT_TYPE: string;
    AUTHORIZATION: string;
    REFRESH_TOKEN: string;
    XSRF_TOKEN: string;
  };
}

interface AuthConfig {
  STORAGE_KEYS: {
    BUSINESS_DETAILS: string;
  };
  ROUTES: {
    LOGIN: string;
    DASHBOARD: string;
    PROFILE: string;
    MAINTENANCE: string;
  };
}

interface MenuItem {
  text: string;
  icon: string;
  route: string;
  new?: boolean;
}

interface UiConfig {
  MENU_ITEMS: MenuItem[];
  COLORS: {
    PRIMARY: string;
    PRIMARY_LIGHT: string;
    PRIMARY_DARK: string;
    SECONDARY: string;
    SECONDARY_LIGHT: string;
    SECONDARY_DARK: string;
    SUCCESS: string;
    ERROR: string;
    WARNING: string;
    INFO: string;
    BACKGROUND: string;
    PAPER: string;
    TEXT_PRIMARY: string;
    TEXT_SECONDARY: string;
    DIVIDER: string;
    PARROT_GREEN: string;
    ORANGE: string;
  };
  TYPOGRAPHY: {
    FONT_FAMILY: string;
    HEADING_FONT_WEIGHT: number;
    BODY_FONT_WEIGHT: number;
  };
  SPACING: {
    UNIT: number;
    SMALL: number;
    MEDIUM: number;
    LARGE: number;
    XLARGE: number;
  };
  BREAKPOINTS: {
    XS: number;
    SM: number;
    MD: number;
    LG: number;
    XL: number;
  };
}

// Normalize VITE_API_URL: "/" or empty means SAME-ORIGIN (nginx proxies /api,
// /oauth2, /login to the gateway). A real URL is used as-is, minus any
// trailing slash, so `${BASE_URL}/path` never produces a protocol-relative
// "//path" URL (which browsers resolve as host "path", e.g.
// http://oauth2/authorization/google).
const RAW_API_BASE: string = import.meta.env.VITE_API_URL ?? '';
export const API_BASE_URL: string =
  !RAW_API_BASE || RAW_API_BASE === '/'
    ? ''
    : RAW_API_BASE.replace(/\/+$/, '');

export const API_CONFIG: ApiConfig = {
  BASE_URL: API_BASE_URL,

  ENDPOINTS: {
    AUTH: {
      HEALTH: '/api/auth/health',
      LOGIN: '/api/auth/authenticate',
      REGISTER: '/api/auth/register',
      REFRESH_TOKEN: '/api/auth/refresh-token',
      LOGOUT: '/api/auth/logout',
      FORGOT_PASSWORD: '/api/auth/forgot-password',
      RESET_PASSWORD: '/api/auth/reset-password',
      GOOGLE_AUTH: '/oauth2/authorization/google',
      ME: '/api/auth/me',
    },

    USER: {
      PROFILE: '/api/User',
      UPDATE_PROFILE: '/api/User/profile/update',
    },

    BUSINESS: {
      ADD: '/api/business/add',
      UPDATE: (id) => `/api/business/update/${id}`,
      GET_ALL: '/api/business/all',
      GET_BY_ID: (id) => `/api/business/get/${id}`,
      ADD_SELF: '/api/business/add',
      ADD_CLIENT: '/api/client/business/add',
      GET_CLIENT_DETAILS: '/api/client/business/all',
      GET_SELF_DETAILS: '/api/business/all',
    },

    BANK: {
      ADD: '/api/banks/add',
      UPDATE: (id) => `/api/banks/${id}/update`,
      GET_ALL: '/api/banks/all',
      GET_BY_ID: (id) => `/api/banks/${id}/get`,
      DELETE: (id) => `/api/banks/${id}/delete`,
    },

    INVOICE: {
      CREATE: '/api/invoices/create',
      GET_ALL: '/api/invoices/all',
      GET_BY_ID: (id) => `/api/invoices/get/${id}`,
      SEARCH: '/api/invoices/search',
      DRAFT: '/api/invoices/draft',
      IN_PROGRESS: '/api/invoices/in-progress',
      UPLOAD: '/api/invoice/upload',
      OCR_STATUS: '/api/invoice/ocr/status',
      JOURNEY: (num) => `/api/invoices/journey/${num}`,
      JOURNEY_COMPLETE: (num) => `/api/invoices/journey/${num}/complete`,
    },

    SUBSCRIPTION: {
      CONFIGURATIONS: '/api/subscriptions/configurations',
      ALL_CONFIGURATIONS: '/api/subscriptions/configurations/all',
      MY: '/api/subscriptions/my',
      MY_ACTIVE: '/api/subscriptions/my/active',
      UPGRADE: '/api/subscriptions/upgrade',
    },

    SUPPORT: {
      INFO: '/api/support/info',
    },

    ADMIN: {
      USERS: '/api/admin/users',
      USER_DETAIL: (userId) => `/api/admin/users/${userId}`,
      USER_INVOICES: (userId) => `/api/admin/users/${userId}/invoices`,
      USER_BUSINESSES: (userId) => `/api/admin/users/${userId}/businesses`,
      USER_CLIENTS: (userId) => `/api/admin/users/${userId}/clients`,
      USER_OVERVIEW: (userId) => `/api/admin/users/${userId}/overview`,
      USER_INACTIVATE: (userId) => `/api/admin/users/${userId}/inactivate`,
      USER_DELETE: (userId) => `/api/admin/users/${userId}/delete`,
      USER_REVOKE_DELETE: (userId) => `/api/admin/users/${userId}/revoke-delete`,
      USER_PERMANENT_DELETE: (userId) => `/api/admin/users/${userId}/permanent-delete`,
      SUPPORT_INFO: '/api/admin/support/info',
    },
  },

  HEADERS: {
    CONTENT_TYPE: 'application/json',
    AUTHORIZATION: 'Authorization',
    REFRESH_TOKEN: 'X-Refresh-Token',
    XSRF_TOKEN: 'X-XSRF-TOKEN',
  },
};

export const AUTH_CONFIG: AuthConfig = {
  STORAGE_KEYS: {
    BUSINESS_DETAILS: 'businessDetails',
  },

  ROUTES: {
    LOGIN: '/login',
    DASHBOARD: '/dashboard',
    PROFILE: '/profile',
    MAINTENANCE: '/maintenance',
  },
};

export const UI_CONFIG: UiConfig = {
  MENU_ITEMS: [
    { text: 'Dashboard', icon: 'Business', route: '/dashboard' },
    { text: 'Invoices', icon: 'Receipt', route: '/invoices', new: true },
    { text: 'New Invoice', icon: 'Description', route: '/invoices/journey', new: true },
    { text: 'Upload Invoice', icon: 'CloudUpload', route: '/invoices/journey?flow=upload', new: true },
    { text: 'Premium Upload', icon: 'Star', route: '/upgrade/ocr', new: true },
    { text: 'Business Details', icon: 'Business', route: '/business-details' },
    { text: 'Client Details', icon: 'Store', route: '/client-details' },
    { text: 'Item Details', icon: 'ListAlt', route: '/item-details' },
    { text: 'Shipping Details', icon: 'Description', route: '/shipping-details' },
    { text: 'Payment Accounts', icon: 'MonetizationOn', route: '/payment-accounts', new: true },
    { text: 'Profile', icon: 'Person', route: '/profile' },
  ],

  COLORS: {
    PRIMARY: '#0b2b4a',
    PRIMARY_LIGHT: '#1a3f64',
    PRIMARY_DARK: '#081f36',
    SECONDARY: '#eab308',
    SECONDARY_LIGHT: '#facc15',
    SECONDARY_DARK: '#ca8a04',
    SUCCESS: '#16a34a',
    ERROR: '#ef4444',
    WARNING: '#f59e0b',
    INFO: '#3b82f6',
    BACKGROUND: '#f8fafc',
    PAPER: '#ffffff',
    TEXT_PRIMARY: '#0f172a',
    TEXT_SECONDARY: '#64748b',
    DIVIDER: '#e2e8f0',
    PARROT_GREEN: '#16a34a',
    ORANGE: '#f59e0b',
  },

  TYPOGRAPHY: {
    FONT_FAMILY: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    HEADING_FONT_WEIGHT: 600,
    BODY_FONT_WEIGHT: 400,
  },

  SPACING: {
    UNIT: 8,
    SMALL: 8,
    MEDIUM: 16,
    LARGE: 24,
    XLARGE: 32,
  },

  BREAKPOINTS: {
    XS: 0,
    SM: 600,
    MD: 960,
    LG: 1280,
    XL: 1920,
  },
};

export default {
  API: API_CONFIG,
  AUTH: AUTH_CONFIG,
  UI: UI_CONFIG,
};
